import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Home, Layers, Building2, Factory } from "lucide-react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

const iconMap = {
  home: Home,
  layers: Layers,
  building: Building2,
  factory: Factory,
};

const defaultServices = [
  { title: "Konut Projeleri", subtitle: "Modern Yaşam Alanları", description: "BCT Yapı olarak, modern mimari anlayışla tasarlanan konut projelerimizde konfor, estetik ve sürdürülebilirliği bir araya getiriyoruz.", image: "https://images.unsplash.com/photo-1769721209842-e46c60e7fbf9?w=800&q=80", icon: "home" },
  { title: "Alt Yapı", subtitle: "Güçlü Temeller", description: "Şehirlerin gelişimi için vazgeçilmez olan altyapı projelerinde uzmanlaşmış ekibimizle yol, köprü, kanalizasyon projeleri gerçekleştiriyoruz.", image: "https://images.pexels.com/photos/4458205/pexels-photo-4458205.jpeg?auto=compress&cs=tinysrgb&w=800", icon: "layers" },
  { title: "Ticari Yapılar", subtitle: "Prestijli İş Merkezleri", description: "İş dünyasının ihtiyaçlarına yönelik tasarladığımız ticari yapılar; ofis binaları, alışveriş merkezleri ve oteller.", image: "https://images.unsplash.com/photo-1664902578190-ec984429fabc?w=800&q=80", icon: "building" },
  { title: "Endüstriyel Yapılar", subtitle: "Üretim ve Lojistik Tesisleri", description: "Endüstriyel sektörün ihtiyaçlarına uygun fabrikalar, depolar ve lojistik merkezleri inşa ediyoruz.", image: "https://images.unsplash.com/photo-1772090131833-f7db87feab28?w=800&q=80", icon: "factory" },
];

export default function Services() {
  const [services, setServices] = useState(defaultServices);
  const [selectedService, setSelectedService] = useState(null);

  useEffect(() => {
    axios.get(`${API}/api/services`).then((r) => {
      if (r.data.length > 0) setServices(r.data);
    }).catch(() => {});
  }, []);

  return (
    <section id="hizmetler" data-testid="services-section" className="py-20 sm:py-28 bg-[#fafafa]">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="mb-14">
          <p className="overline mb-5">Hizmetlerimiz</p>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tighter text-[#0C0C0C]" style={{ fontFamily: "Outfit" }}>
            Uzmanlık <span className="text-[#FF5A00]">alanlarımız</span>
          </h2>
        </motion.div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {services.map((svc, i) => {
            const Icon = iconMap[svc.icon] || Home;
            return (
              <motion.div
                key={svc.id || i}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1, duration: 0.5 }}
                onClick={() => setSelectedService(svc)}
                className="group cursor-pointer bg-white rounded-2xl overflow-hidden border border-gray-100 hover:border-[#FF5A00]/20 transition-all duration-300 hover:shadow-xl hover:-translate-y-1"
                data-testid={`service-card-${i}`}
              >
                <div className="relative h-44 overflow-hidden">
                  <img src={svc.image} alt={svc.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                  <div className="absolute bottom-4 left-4">
                    <div className="w-10 h-10 rounded-xl bg-[#FF5A00] flex items-center justify-center mb-2">
                      <Icon size={20} className="text-white" />
                    </div>
                  </div>
                </div>
                <div className="p-5">
                  <h3 className="text-base font-bold text-[#0C0C0C] mb-1" style={{ fontFamily: "Outfit" }}>{svc.title}</h3>
                  <p className="text-xs text-[#FF5A00] font-semibold uppercase tracking-wider">{svc.subtitle}</p>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>

      {/* Service Detail Modal */}
      <AnimatePresence>
        {selectedService && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 z-[100] flex items-center justify-center p-4"
            onClick={() => setSelectedService(null)}
            data-testid="service-modal"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 30 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 30 }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
              className="bg-white rounded-2xl w-full max-w-2xl max-h-[85vh] overflow-hidden shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal Image */}
              <div className="relative h-56 sm:h-64 overflow-hidden">
                <img src={selectedService.image} alt={selectedService.title} className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
                <button
                  onClick={() => setSelectedService(null)}
                  className="absolute top-4 right-4 w-10 h-10 bg-black/40 hover:bg-black/60 text-white rounded-full flex items-center justify-center transition-colors"
                  data-testid="service-modal-close"
                >
                  <X size={20} />
                </button>
                <div className="absolute bottom-5 left-6">
                  <div className="flex items-center gap-3 mb-2">
                    {(() => {
                      const Icon = iconMap[selectedService.icon] || Home;
                      return (
                        <div className="w-11 h-11 rounded-xl bg-[#FF5A00] flex items-center justify-center">
                          <Icon size={22} className="text-white" />
                        </div>
                      );
                    })()}
                    <div>
                      <h2 className="text-2xl font-bold text-white" style={{ fontFamily: "Outfit" }}>{selectedService.title}</h2>
                      <p className="text-sm text-white/70 font-medium">{selectedService.subtitle}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Modal Content */}
              <div className="p-6 sm:p-8 overflow-y-auto max-h-[calc(85vh-16rem)]">
                <p className="text-gray-600 leading-relaxed text-[15px]">{selectedService.description}</p>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
